import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import {Controller, useForm, useWatch} from "react-hook-form";
import toast from "react-hot-toast";

import {classNames} from "../../../utils/Utils";
import InputDate from "../../../components/common/form/input-date";
import InputTextarea from "../../../components/common/form/input-textarea";
import InputSelect from "../../../components/common/form/input-select";
import InputUserSelect from "../../../components/common/form/input-user-select";
import ExchangeRateBtn from "../../../components/common/form/exchage-rate-btn";
import FormPopup from "../../../components/common/popup/form-popup";
import {getElement, getWarehouses, postElement} from "../../../api/config";
import {
    COMPANY,
    COMPANY_PAYMENT_TERM,
    COMPANY_PAYMENT_TERMS,
    COMPANY_VAT,
    COMPANY_VATS,
    EXCHANGE,
} from "../../../api/endpoints";
import {getPathParam} from "../../../utils/converter";
import {
    AUTO_SAVE_DELAY,
    HEADER_COMPACT_CLASS,
    buildDocumentFormDefaults,
    buildPaymentFormDefaults,
    sanitizeFormValues,
    sanitizePaymentConditions,
    toPaymentTermOption,
    toVatOption,
    toWarehouseOption,
} from "./document-header-utils";
import PaymentTermModalForm from "./document-payment-term-modal-form";
import VatModalForm from "./document-vat-modal-form";

const DocumentForm = ({
    document,
    onSubmit,
    isSubmitting,
    termTypesList,
    isTermTypesListLoading,
    currenciesList,
    isCurrenciesListLoading,
    company,
    companyId,
    isEditable,
    isVatEnabled,
    isPaymentTermsEnabled,
    isWarehouseEnabled,
}) => {
    const {t} = useTranslation();
    const excludedUserId = "";
    const isFormEditable = Boolean(isEditable && typeof onSubmit === "function");

    const normalizeDocumentUser = useCallback((entry) => {
        if (!entry) {
            return null;
        }
        const user = entry.user ?? entry;
        if (!user?.id) {
            return null;
        }
        const combinedName = [user.firstname, user.lastname].filter(Boolean).join(" ").trim();
        const fullName = user.fullname
            ?? (combinedName !== "" ? combinedName : null)
            ?? user.name
            ?? user.email
            ?? "";

        return {
            ...user,
            fullname: fullName,
        };
    }, []);

    const documentUsersFromProps = useMemo(() => {
        const rawDocumentUsers = Array.isArray(document?.document_users) ? document.document_users : [];
        const rawUsers = Array.isArray(document?.users) ? document.users : [];
        const uniqueUsers = new Map();

        [...rawDocumentUsers, ...rawUsers].forEach((item) => {
            const normalized = normalizeDocumentUser(item);
            if (normalized?.id && !uniqueUsers.has(normalized.id)) {
                uniqueUsers.set(normalized.id, normalized);
            }
        });

        return Array.from(uniqueUsers.values());
    }, [document, normalizeDocumentUser]);

    const areUserListsEqual = useCallback((left = [], right = []) => {
        const toIdList = (source) => (source ?? [])
            .map((item) => item?.id)
            .filter((id) => id !== undefined && id !== null)
            .map((id) => String(id));

        const leftIds = toIdList(left);
        const rightIds = toIdList(right);

        if (leftIds.length !== rightIds.length) {
            return false;
        }

        const rightSet = new Set(rightIds);
        return leftIds.every((id) => rightSet.has(id));
    }, []);

    const formDefaults = useMemo(() => ({
        ...buildDocumentFormDefaults(document),
        ...buildPaymentFormDefaults(document),
        document_users: documentUsersFromProps,
    }), [document, documentUsersFromProps]);

    const {
        register,
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: {errors, isDirty},
    } = useForm({
        defaultValues: formDefaults,
    });
    const [selectedUsers, setSelectedUsers] = useState(documentUsersFromProps);
    const [warehouseList, setWarehouseList] = useState([]);
    const [isWarehouseLoading, setWarehouseLoading] = useState(false);

    useEffect(() => {
        register("document_users");
    }, [register]);

    useEffect(() => {
        setSelectedUsers((prev) => (
            areUserListsEqual(prev, documentUsersFromProps) ? prev : documentUsersFromProps
        ));
        setValue("document_users", documentUsersFromProps, {shouldDirty: false});
    }, [areUserListsEqual, documentUsersFromProps, setValue]);

    useEffect(() => {
        if (areUserListsEqual(selectedUsers, documentUsersFromProps)) {
            return;
        }
        setValue("document_users", selectedUsers, {shouldDirty: true});
    }, [areUserListsEqual, documentUsersFromProps, selectedUsers, setValue]);

    const autoSaveTimer = useRef(null);
    const hasInitializedExchangeCleanupRef = useRef(false);
    const watchedValues = useWatch({
        control,
        name: [
            "registration_date",
            "document_date",
            "expire_date",
            "next_reminder",
            "terms_type",
            "document_note",
            "internal_note",
            "connection_note",
            "currency",
            "exchange_date",
            "exchange_rate",
            "payment_term",
            "company_vat",
            "document_users",
            "company_warehouse",
        ],
    });

    const handleUsersSelect = useCallback((users) => {
        const nextSelection = Array.isArray(users)
            ? users
            : (users ? [users] : []);
        const uniqueUsers = new Map();

        nextSelection.forEach((item) => {
            const normalized = normalizeDocumentUser(item);
            if (normalized?.id && !uniqueUsers.has(normalized.id)) {
                uniqueUsers.set(normalized.id, normalized);
            }
        });

        setSelectedUsers(Array.from(uniqueUsers.values()));
    }, [normalizeDocumentUser]);

    const [isExchangeLoading, setExchangeLoading] = useState(false);
    const [paymentTermList, setPaymentTermList] = useState([]);
    const [isPaymentTermsLoading, setPaymentTermsLoading] = useState(false);
    const [vatList, setVatList] = useState([]);
    const [isVatLoading, setVatLoading] = useState(false);
    const [isPaymentTermModalOpen, setPaymentTermModalOpen] = useState(false);
    const [paymentTermError, setPaymentTermError] = useState(null);
    const [isPaymentTermSaving, setPaymentTermSaving] = useState(false);
    const [isVatModalOpen, setVatModalOpen] = useState(false);
    const [vatError, setVatError] = useState(null);
    const [isVatSaving, setVatSaving] = useState(false);
    const [paymentTermModalKey, setPaymentTermModalKey] = useState(0);
    const [vatModalKey, setVatModalKey] = useState(0);

    useEffect(() => {
        reset(formDefaults);
    }, [formDefaults, reset]);

    const hasCompanyContext = Boolean(companyId);
    const companyCurrencyCode = company?.currency?.code ?? null;
    const selectedCurrency = watch("currency");
    const exchangeDateValue = watch("exchange_date");
    const exchangeRateValue = watch("exchange_rate");
    const paymentTermValue = watch("payment_term");
    const companyVatValue = watch("company_vat");
    const companyWarehouseValue = watch("company_warehouse");
    const companyCurrencyCodeUpper = typeof companyCurrencyCode === "string" ? companyCurrencyCode.toUpperCase() : null;
    const selectedCurrencyCodeUpper = typeof selectedCurrency?.code === "string" ? selectedCurrency.code.toUpperCase() : null;
    const shouldShowExchangeSection = Boolean(
        selectedCurrencyCodeUpper
        && companyCurrencyCodeUpper
        && selectedCurrencyCodeUpper !== companyCurrencyCodeUpper
    );

    const paymentTermOptions = useMemo(() => {
        const map = new Map();
        (paymentTermList ?? []).forEach((option) => {
            if (option?.value) {
                map.set(option.value, option);
            }
        });
        if (paymentTermValue?.value && !map.has(paymentTermValue.value)) {
            map.set(paymentTermValue.value, paymentTermValue);
        }
        return Array.from(map.values());
    }, [paymentTermList, paymentTermValue]);

    const vatOptions = useMemo(() => {
        const map = new Map();
        (vatList ?? []).forEach((option) => {
            if (option?.value) {
                map.set(option.value, option);
            }
        });
        if (companyVatValue?.value && !map.has(companyVatValue.value)) {
            map.set(companyVatValue.value, companyVatValue);
        }
        return Array.from(map.values());
    }, [vatList, companyVatValue]);

    const warehouseOptions = useMemo(() => {
        const map = new Map();
        (warehouseList ?? []).forEach((option) => {
            if (option?.value) {
                map.set(option.value, option);
            }
        });
        const currentWarehouse = companyWarehouseValue;
        const currentWarehouseId = currentWarehouse?.value ?? currentWarehouse?.id ?? null;
        if (currentWarehouseId && !map.has(currentWarehouseId)) {
            map.set(currentWarehouseId, currentWarehouse);
        }
        return Array.from(map.values());
    }, [warehouseList, companyWarehouseValue]);

    useEffect(() => {
        const shouldMarkDirty = hasInitializedExchangeCleanupRef.current;

        if (!shouldShowExchangeSection) {
            if (exchangeDateValue) {
                setValue("exchange_date", null, {shouldDirty: shouldMarkDirty});
            }

            const hasExchangeRateValue = exchangeRateValue !== null && exchangeRateValue !== undefined && exchangeRateValue !== "";
            if (hasExchangeRateValue) {
                setValue("exchange_rate", null, {shouldDirty: shouldMarkDirty});
            }
        }

        if (!hasInitializedExchangeCleanupRef.current) {
            hasInitializedExchangeCleanupRef.current = true;
        }
    }, [exchangeDateValue, exchangeRateValue, setValue, shouldShowExchangeSection]);

    useEffect(() => {
        if (!hasCompanyContext || !isPaymentTermsEnabled) {
            setPaymentTermModalOpen(false);
        }
        if (!hasCompanyContext || !isVatEnabled) {
            setVatModalOpen(false);
        }
    }, [hasCompanyContext, isPaymentTermsEnabled, isVatEnabled]);

    useEffect(() => {
        if (!companyId || !isWarehouseEnabled) {
            setWarehouseList([]);
            setWarehouseLoading(false);
            return;
        }

        let isActive = true;
        const controller = new AbortController();
        const {signal} = controller;

        setWarehouseLoading(true);
        getWarehouses({company_id: companyId, params: null, signal})
            .then((response) => {
                if (!isActive) {
                    return;
                }
                const warehouses = Array.isArray(response?.warehouses) ? response.warehouses : [];
                setWarehouseList(warehouses.map((item) => toWarehouseOption(item)).filter(Boolean));
            })
            .catch((error) => {
                if (!isActive) {
                    return;
                }
                if (error?.name === "AbortError") {
                    return;
                }
                toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
                setWarehouseList([]);
            })
            .finally(() => {
                if (isActive) {
                    setWarehouseLoading(false);
                }
            });

        return () => {
            isActive = false;
            controller.abort();
        };
    }, [companyId, isWarehouseEnabled, t]);

    useEffect(() => {
        if (!companyId || !isPaymentTermsEnabled) {
            setPaymentTermList([]);
            setPaymentTermsLoading(false);
            return;
        }

        let isActive = true;
        setPaymentTermsLoading(true);
        const endpoint = getPathParam([COMPANY, String(companyId), COMPANY_PAYMENT_TERMS]);

        getElement(endpoint)
            .then((response) => {
                if (!isActive) {
                    return;
                }
                const terms = Array.isArray(response?.payment_terms) ? response.payment_terms : [];
                setPaymentTermList(terms.map((item) => toPaymentTermOption(item)).filter(Boolean));
            })
            .catch((error) => {
                if (isActive) {
                    toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
                    setPaymentTermList([]);
                }
            })
            .finally(() => {
                if (isActive) {
                    setPaymentTermsLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [companyId, isPaymentTermsEnabled, t]);

    useEffect(() => {
        if (!companyId || !isVatEnabled) {
            setVatList([]);
            setVatLoading(false);
            return;
        }

        let isActive = true;
        setVatLoading(true);
        const endpoint = getPathParam([COMPANY, String(companyId), COMPANY_VATS]);

        getElement(endpoint)
            .then((response) => {
                if (!isActive) {
                    return;
                }
                const vats = Array.isArray(response?.company_vats) ? response.company_vats : [];
                setVatList(vats.map((item) => toVatOption(item)).filter(Boolean));
            })
            .catch((error) => {
                if (isActive) {
                    toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
                    setVatList([]);
                }
            })
            .finally(() => {
                if (isActive) {
                    setVatLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [companyId, isVatEnabled, t]);

    const submit = useCallback((values) => {
        if (!isFormEditable || typeof onSubmit !== "function") {
            return;
        }

        const payload = {...values};
        const mergePayload = {...values};

        if (values.terms_type) {
            const termsTypeId = values.terms_type.value ?? values.terms_type.id ?? null;
            payload.terms_type = termsTypeId ? {id: termsTypeId} : null;
            mergePayload.terms_type = termsTypeId
                ? {
                    id: termsTypeId,
                    name: values.terms_type.label ?? values.terms_type.name ?? null,
                    code: values.terms_type.code ?? null,
                }
                : null;
        } else {
            payload.terms_type = null;
            mergePayload.terms_type = null;
        }

        if (values.currency) {
            const currencyId = values.currency.value ?? values.currency.id ?? null;
            payload.currency = currencyId ? {id: currencyId} : null;
            mergePayload.currency = values.currency.data ?? (
                currencyId
                    ? {
                        id: currencyId,
                        code: values.currency.code ?? values.currency.label ?? null,
                    }
                    : null
            );
        } else {
            payload.currency = null;
            mergePayload.currency = null;
        }

        if (values.payment_term) {
            const paymentTermId = values.payment_term.value ?? values.payment_term.id ?? null;
            const normalizedPaymentTerm = paymentTermId ? {id: paymentTermId} : null;
            payload.company_payment_terms = normalizedPaymentTerm;
            const enrichedPaymentTerm = values.payment_term.data ?? (
                paymentTermId
                    ? {
                        id: paymentTermId,
                        name: values.payment_term.label ?? null,
                        code: values.payment_term.code ?? null,
                    }
                    : null
            );
            mergePayload.company_payment_terms = enrichedPaymentTerm;
            mergePayload.company_payment_term = enrichedPaymentTerm;
        } else {
            payload.company_payment_terms = null;
            mergePayload.company_payment_terms = null;
            mergePayload.company_payment_term = null;
        }

        if (values.company_vat) {
            const vatId = values.company_vat.value ?? values.company_vat.id ?? null;
            payload.company_vat = vatId ? {id: vatId} : null;
            mergePayload.company_vat = values.company_vat.data ?? (
                vatId
                    ? {
                        id: vatId,
                        name: values.company_vat.label ?? null,
                        code: values.company_vat.code ?? null,
                        value: values.company_vat.rate ?? null,
                    }
                    : null
            );
        } else {
            payload.company_vat = null;
            mergePayload.company_vat = null;
        }

        if (values.company_warehouse) {
            const warehouseId = values.company_warehouse.value ?? values.company_warehouse.id ?? null;
            const normalizedWarehouse = warehouseId ? {id: warehouseId} : null;
            payload.company_warehouse = normalizedWarehouse;
            mergePayload.company_warehouse = values.company_warehouse.data ?? (
                warehouseId
                    ? {
                        id: warehouseId,
                        name: values.company_warehouse.label ?? values.company_warehouse.name ?? null,
                    }
                    : null
            );
        } else {
            payload.company_warehouse = null;
            mergePayload.company_warehouse = null;
        }

        delete payload.document_users;
        delete payload.users_to_remove;
        delete mergePayload.document_users;
        delete mergePayload.users_to_remove;
        delete payload.payment_term;

        const selectedUsersMap = new Map();
        (selectedUsers ?? []).forEach((userItem) => {
            const normalized = normalizeDocumentUser(userItem);
            if (normalized?.id && !selectedUsersMap.has(normalized.id)) {
                selectedUsersMap.set(normalized.id, normalized);
            }
        });
        const selectedUsersList = Array.from(selectedUsersMap.values());
        const selectedUserIdSet = new Set(
            selectedUsersList
                .map((userItem) => userItem?.id)
                .filter((id) => id !== undefined && id !== null)
                .map((id) => String(id))
        );

        if (selectedUsersList.length > 0) {
            payload.document_users = selectedUsersList.map((userItem) => ({
                user: {id: userItem.id},
            }));
        }

        const usersToRemove = (documentUsersFromProps ?? [])
            .filter((userItem) => {
                const userId = userItem?.id;
                if (userId === undefined || userId === null) {
                    return false;
                }
                return !selectedUserIdSet.has(String(userId));
            })
            .map((userItem) => ({
                user: {id: userItem.id},
            }));

        if (usersToRemove.length > 0) {
            payload.users_to_remove = usersToRemove;
        }

        mergePayload.document_users = selectedUsersList.map((userItem) => ({
            user: {...userItem},
        }));

        const sanitizedPayload = sanitizeFormValues(payload);

        onSubmit({
            payload: sanitizedPayload,
            merge: mergePayload,
        });
    }, [documentUsersFromProps, isFormEditable, normalizeDocumentUser, onSubmit, selectedUsers]);

    useEffect(() => {
        if (!isFormEditable || !isDirty || isSubmitting) {
            return undefined;
        }

        if (autoSaveTimer.current) {
            clearTimeout(autoSaveTimer.current);
        }

        autoSaveTimer.current = setTimeout(() => {
            handleSubmit(submit)();
            autoSaveTimer.current = null;
        }, AUTO_SAVE_DELAY);

        return () => {
            if (autoSaveTimer.current) {
                clearTimeout(autoSaveTimer.current);
                autoSaveTimer.current = null;
            }
        };
    }, [watchedValues, isDirty, isSubmitting, handleSubmit, submit, isFormEditable]);

    const handleFetchExchangeRate = useCallback(async () => {
        const currencyValue = watch("currency");
        const targetCurrencyCode = typeof currencyValue?.code === "string"
            ? currencyValue.code.toUpperCase()
            : null;
        if (!targetCurrencyCode || !companyCurrencyCodeUpper) {
            return;
        }
        setExchangeLoading(true);
        try {
            const response = await getElement(getPathParam([EXCHANGE, targetCurrencyCode, companyCurrencyCodeUpper]));
            if (response?.rate !== undefined && response?.rate !== null) {
                setValue("exchange_rate", response.rate);
            }
        } catch (error) {
            toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
        } finally {
            setExchangeLoading(false);
        }
    }, [companyCurrencyCodeUpper, setValue, t, watch]);

    const upsertPaymentTermOption = useCallback((option) => {
        if (!option?.value) {
            return;
        }
        setPaymentTermList((prev) => {
            const map = new Map();
            (prev ?? []).forEach((item) => {
                if (item?.value) {
                    map.set(item.value, item);
                }
            });
            map.set(option.value, option);
            return Array.from(map.values());
        });
    }, []);

    const upsertVatOption = useCallback((option) => {
        if (!option?.value) {
            return;
        }
        setVatList((prev) => {
            const map = new Map();
            (prev ?? []).forEach((item) => {
                if (item?.value) {
                    map.set(item.value, item);
                }
            });
            map.set(option.value, option);
            return Array.from(map.values());
        });
    }, []);

    const handleCreatePaymentTerm = useCallback(async (data) => {
        if (!isPaymentTermsEnabled) {
            return;
        }
        if (!companyId) {
            setPaymentTermError(t("document.form.payment_company_hint", {defaultValue: "Collega prima un'azienda per selezionare i termini di pagamento"}));
            return;
        }

        setPaymentTermSaving(true);
        setPaymentTermError(null);
        const endpoint = getPathParam([COMPANY, String(companyId), COMPANY_PAYMENT_TERM]);
        const payload = {
            name: data.name?.trim(),
            code: data.code?.trim(),
            description: data.description?.trim() || null,
            condition_type: sanitizePaymentConditions(data.condition_type),
        };

        try {
            const response = await postElement(endpoint, payload);
            const created = response?.payment_term ?? response;
            const option = toPaymentTermOption(created);
            if (option) {
                upsertPaymentTermOption(option);
                setValue("payment_term", option, {shouldDirty: true});
            }
            toast.success(t("document.form.payment_term_create_success", {defaultValue: "Termine di pagamento creato"}));
            setPaymentTermModalOpen(false);
            setPaymentTermModalKey((key) => key + 1);
        } catch (error) {
            setPaymentTermError(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
        } finally {
            setPaymentTermSaving(false);
        }
    }, [companyId, isPaymentTermsEnabled, setValue, t, upsertPaymentTermOption]);

    const handleCreateVat = useCallback(async (data) => {
        if (!isVatEnabled) {
            return;
        }
        if (!companyId) {
            setVatError(t("document.form.payment_company_hint", {defaultValue: "Collega prima un'azienda per selezionare i termini di pagamento"}));
            return;
        }

        setVatSaving(true);
        setVatError(null);
        const endpoint = getPathParam([COMPANY, String(companyId), COMPANY_VAT]);
        const payload = {
            name: data.name?.trim(),
            code: data.code?.trim(),
            description: data.description?.trim() || null,
            value: data.value === null || data.value === undefined ? null : Number(data.value),
        };

        try {
            const response = await postElement(endpoint, payload);
            const created = response?.vat ?? response;
            const option = toVatOption(created);
            if (option) {
                upsertVatOption(option);
                setValue("company_vat", option, {shouldDirty: true});
            }
            toast.success(t("document.form.vat_create_success", {defaultValue: "Aliquota IVA creata"}));
            setVatModalOpen(false);
            setVatModalKey((key) => key + 1);
        } catch (error) {
            setVatError(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
        } finally {
            setVatSaving(false);
        }
    }, [companyId, isVatEnabled, setValue, t, upsertVatOption]);

    return (
        <>
            <form
                onSubmit={handleSubmit(submit)}
                className={classNames("space-y-5", HEADER_COMPACT_CLASS)}
            >
                <fieldset disabled={!isFormEditable} className={isFormEditable ? "" : "opacity-70"}>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-6 xl:grid-cols-12 max-h-[300px] overflow-x-hidden overflow-y-auto px-0.5">
                    <Controller
                        name="registration_date"
                        control={control}
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                            <div className="sm:col-span-3">
                                <InputDate
                                    label={t("document.form.registration_date", {defaultValue: "Data registrazione"})}
                                    name={name}
                                    startDate={value}
                                    onChange={onChange}
                                    errors={error}
                                    isShowTime={false}
                                />
                            </div>
                        )}
                    />

                    <Controller
                        name="document_date"
                        control={control}
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                            <div className="sm:col-span-3">
                                <InputDate
                                    label={t("document.form.document_date", {defaultValue: "Data documento"})}
                                    name={name}
                                    startDate={value}
                                    onChange={onChange}
                                    errors={error}
                                    isShowTime={false}
                                />
                            </div>
                        )}
                    />

                    <Controller
                        name="expire_date"
                        control={control}
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                            <div className="sm:col-span-3">
                                <InputDate
                                    label={t("document.form.expire_date", {defaultValue: "Data scadenza"})}
                                    name={name}
                                    startDate={value}
                                    onChange={onChange}
                                    errors={error}
                                    isShowTime={false}
                                />
                            </div>
                        )}
                    />

                    <Controller
                        name="next_reminder"
                        control={control}
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                            <div className="sm:col-span-3">
                                <InputDate
                                    label={t("document.form.next_reminder", {defaultValue: "Prossimo promemoria"})}
                                    name={name}
                                    startDate={value}
                                    onChange={onChange}
                                    errors={error}
                                    isShowTime={false}
                                />
                            </div>
                        )}
                    />

                    <Controller
                        name="terms_type"
                        control={control}
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                            <div className="sm:col-span-6 xl:col-span-3">
                                <InputSelect
                                    label={t("document.form.incoterms", {defaultValue: "Incoterms"})}
                                    name={name}
                                    onChange={onChange}
                                    options={termTypesList}
                                    value={value}
                                    isLoading={isTermTypesListLoading}
                                    isDisabled={isTermTypesListLoading}
                                    errors={error}
                                    isRequired
                                    size="compact"
                                />
                            </div>
                        )}
                    />

                    <div className="sm:col-span-6 xl:col-span-3">
                        <InputTextarea
                            id="document_note"
                            errors={errors.document_note}
                            inputClassName="document-header-note"
                            input={{
                                ...register("document_note"),
                                rows: 2,
                            }}
                            label={t("document.form.document_note", {defaultValue: "Note documento"})}
                        />
                    </div>

                    <div className="sm:col-span-6 xl:col-span-3">
                        <InputTextarea
                            id="connection_note"
                            errors={errors.connection_note}
                            inputClassName="document-header-note"
                            input={{
                                ...register("connection_note"),
                                rows: 2,
                            }}
                            label={t("document.form.connection_note", {defaultValue: "Note per il cliente"})}
                        />
                    </div>

                    <div className="sm:col-span-6 xl:col-span-3">
                        <InputTextarea
                            id="internal_note"
                            errors={errors.internal_note}
                            inputClassName="document-header-note"
                            input={{
                                ...register("internal_note"),
                                rows: 2,
                            }}
                            label={t("document.form.internal_note", {defaultValue: "Note interne"})}
                        />
                    </div>

                    {isWarehouseEnabled && (
                        <Controller
                            name="company_warehouse"
                            control={control}
                            render={({field: {onChange, value, name}, fieldState: {error}}) => (
                                <div className="sm:col-span-6 xl:col-span-4">
                                    <InputSelect
                                        label={t("app.warehouse.warehouse", {defaultValue: "Warehouse"})}
                                        name={name}
                                        options={warehouseOptions}
                                        value={value}
                                        onChange={(selected) => {
                                            onChange(selected);
                                            setValue(name, selected, {shouldDirty: true});
                                        }}
                                        isLoading={isWarehouseLoading}
                                        isDisabled={!hasCompanyContext || isWarehouseLoading}
                                        errors={error}
                                        isClearable
                                        size="compact"
                                    />
                                    {!hasCompanyContext && (
                                        <p className="mt-2 text-xs text-gray-500">
                                            {t("document.form.warehouse_company_hint", {defaultValue: "Link a company before selecting a warehouse"})}
                                        </p>
                                    )}
                                </div>
                            )}
                        />
                    )}

                    {isPaymentTermsEnabled && (
                        <Controller
                            name="payment_term"
                            control={control}
                            render={({field: {onChange, value, name}, fieldState: {error}}) => (
                                <div className="sm:col-span-6 xl:col-span-4">
                                    <div className=" flex items-center justify-between">
                                        <label className="block text-sm font-medium text-gray-700">
                                            {t("document.form.payment_terms", {defaultValue: "Termini di pagamento"})}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (!hasCompanyContext) {
                                                    return;
                                                }
                                                setPaymentTermError(null);
                                                setPaymentTermModalOpen(true);
                                            }}
                                            disabled={!hasCompanyContext || isPaymentTermSaving}
                                            className="text-xs font-medium text-blue-1000 hover:text-blue-900 disabled:cursor-not-allowed disabled:text-gray-400"
                                        >
                                            {t("document.form.add_payment_term_action", {defaultValue: "Add payment term"})}
                                        </button>
                                    </div>
                                    <InputSelect
                                        isShowLabel={false}
                                        name={name}
                                        label={t("document.form.payment_terms", {defaultValue: "Payment terms"})}
                                        options={paymentTermOptions}
                                        value={value}
                                        onChange={(selected) => {
                                            onChange(selected);
                                            setValue(name, selected, {shouldDirty: true});
                                        }}
                                        isLoading={isPaymentTermsLoading}
                                        isDisabled={!hasCompanyContext || isPaymentTermsLoading}
                                        errors={error}
                                        isClearable
                                        size="compact"
                                    />
                                    {!hasCompanyContext && (
                                        <p className="mt-2 text-xs text-gray-500">
                                            {t("document.form.payment_company_hint", {defaultValue: "Link a company before selecting payment data"})}
                                        </p>
                                    )}
                                </div>
                            )}
                        />
                    )}

                    {isVatEnabled && (
                        <Controller
                            name="company_vat"
                            control={control}
                            render={({field: {onChange, value, name}, fieldState: {error}}) => (
                                <div className="sm:col-span-6 xl:col-span-4">
                                    <div className=" flex items-center justify-between">
                                        <label className="block text-sm font-medium text-gray-700">
                                            {t("document.form.company_vat", {defaultValue: "VAT"})}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (!hasCompanyContext) {
                                                    return;
                                                }
                                                setVatError(null);
                                                setVatModalOpen(true);
                                            }}
                                            disabled={!hasCompanyContext || isVatSaving}
                                            className="text-xs font-medium text-blue-1000 hover:text-blue-900 disabled:cursor-not-allowed disabled:text-gray-400"
                                        >
                                            {t("document.form.add_vat_action", {defaultValue: "Add VAT"})}
                                        </button>
                                    </div>
                                    <InputSelect
                                        isShowLabel={false}
                                        name={name}
                                        label={t("document.form.company_vat", {defaultValue: "VAT"})}
                                        options={vatOptions}
                                        value={value}
                                        onChange={(selected) => {
                                            onChange(selected);
                                            setValue(name, selected, {shouldDirty: true});
                                        }}
                                        isLoading={isVatLoading}
                                        isDisabled={!hasCompanyContext || isVatLoading}
                                        errors={error}
                                        isClearable
                                        size="compact"
                                    />
                                    {!hasCompanyContext && (
                                        <p className="mt-2 text-xs text-gray-500">
                                            {t("document.form.payment_company_hint", {defaultValue: "Link a company before selecting payment data"})}
                                        </p>
                                    )}
                                </div>
                            )}
                        />
                    )}

                    <div className="sm:col-span-6 xl:col-span-4">
                        <InputUserSelect
                            label={t("app.users.users")}
                            placeholder=""
                            multiple
                            size={'compact'}
                            includeAll={true}
                            excludeId={excludedUserId}
                            selectedUsers={selectedUsers}
                            onSelect={handleUsersSelect}
                            dropdownPlacement="top"
                        />
                    </div>

                    <Controller
                        name="currency"
                        control={control}
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                            <div className="sm:col-span-6 xl:col-span-4">
                                <InputSelect
                                    label={t("document.form.currency", {defaultValue: "Valuta"})}
                                    name={name}
                                    onChange={(selected) => {
                                        onChange(selected);
                                        setValue(name, selected, {shouldDirty: true});
                                    }}
                                    options={currenciesList}
                                    value={value}
                                    isLoading={isCurrenciesListLoading}
                                    isDisabled={isCurrenciesListLoading}
                                    errors={error}
                                    isRequired
                                    size="compact"
                                />
                            </div>
                        )}
                    />

                    {shouldShowExchangeSection && (
                        <Controller
                            name="exchange_date"
                            control={control}
                            render={({field: {onChange, value, name}, fieldState: {error}}) => (
                                <div className="sm:col-span-6 xl:col-span-3">
                                    <InputDate
                                        label={t("document.form.exchange_date", {defaultValue: "Data tasso di cambio"})}
                                        name={name}
                                        startDate={value}
                                        onChange={onChange}
                                        errors={error}
                                        isShowTime={false}
                                        isRequired
                                    />
                                </div>
                            )}
                        />
                    )}

                    {shouldShowExchangeSection && (
                        <div className="sm:col-span-6 xl:col-span-3">
                            <label className="block text-sm font-medium mb-1 text-gray-700">
                                {t("app.quotations.change_rate_title", {
                                    currency_1: `1 ${selectedCurrencyCodeUpper ?? selectedCurrency?.code}`,
                                    currency_2: `1 ${companyCurrencyCodeUpper ?? companyCurrencyCode}`,
                                })}
                            </label>
                            <Controller
                                name="exchange_rate"
                                rules={{required: true}}
                                control={control}
                                render={({field: {onChange, value, name}, fieldState: {error}}) => (
                                    <ExchangeRateBtn
                                        label={t("app.quotations.change_rate_label", {defaultValue: "Tasso di cambio"})}
                                        isShowLabel={false}
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        thousandSeparator={"."}
                                        decimalSeparator={","}
                                        suffix={` ${company?.currency?.currency_symbol ?? ""}`}
                                        errors={error}
                                        decimalScale={6}
                                        isRequired
                                        isLoading={isExchangeLoading}
                                        onClick={handleFetchExchangeRate}
                                    />
                                )}
                            />
                        </div>
                    )}

                </div>
                </fieldset>

            </form>

            {isPaymentTermsEnabled && (
                <FormPopup
                    title={t("document.form.payment_term_form_title", {defaultValue: "Aggiungi termine di pagamento"})}
                    isOpen={isPaymentTermModalOpen}
                    setIsOpen={(open) => {
                        if (!open && !isPaymentTermSaving) {
                            setPaymentTermModalOpen(false);
                            setPaymentTermError(null);
                            setPaymentTermModalKey((key) => key + 1);
                        }
                    }}
                    isActiveCloseBack={!isPaymentTermSaving}
                >
                    <PaymentTermModalForm
                        key={paymentTermModalKey}
                        exception={paymentTermError}
                        isLoading={isPaymentTermSaving}
                        onSubmitData={handleCreatePaymentTerm}
                    />
                </FormPopup>
            )}

            {isVatEnabled && (
                <FormPopup
                    title={t("document.form.vat_form_title", {defaultValue: "Aggiungi IVA"})}
                    isOpen={isVatModalOpen}
                    setIsOpen={(open) => {
                        if (!open && !isVatSaving) {
                            setVatModalOpen(false);
                            setVatError(null);
                            setVatModalKey((key) => key + 1);
                        }
                    }}
                    isActiveCloseBack={!isVatSaving}
                >
                    <VatModalForm
                        key={vatModalKey}
                        exception={vatError}
                        isLoading={isVatSaving}
                        onSubmitData={handleCreateVat}
                    />
                </FormPopup>
            )}
        </>
    );
};

DocumentForm.propTypes = {
    document: PropTypes.object,
    onSubmit: PropTypes.func,
    isSubmitting: PropTypes.bool,
    termTypesList: PropTypes.array,
    isTermTypesListLoading: PropTypes.bool,
    currenciesList: PropTypes.array,
    isCurrenciesListLoading: PropTypes.bool,
    company: PropTypes.object,
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isEditable: PropTypes.bool,
    isVatEnabled: PropTypes.bool,
    isPaymentTermsEnabled: PropTypes.bool,
    isWarehouseEnabled: PropTypes.bool,
};

DocumentForm.defaultProps = {
    document: null,
    onSubmit: null,
    isSubmitting: false,
    termTypesList: [],
    isTermTypesListLoading: false,
    currenciesList: [],
    isCurrenciesListLoading: false,
    company: null,
    companyId: null,
    isEditable: true,
    isVatEnabled: true,
    isPaymentTermsEnabled: true,
    isWarehouseEnabled: true,
};

export default DocumentForm;
